import { useEffect } from "react";
import useClarity from "hooks/useClarity";
import usePlotline from "hooks/usePlotline";
import { useSingular } from "../../hooks/useSingular";
import { useHandlePlotlineEventsDataMutation } from "../../redux/reduxAPI";
import { CreatorAuthorizations, CreatorConfig } from "../../types/creator";
import { triggerGAUserId } from "../../utils/common";
import { setUserPropertiesGa } from "../../utils/gaUtils";

type Props = {
  creatorConfig?: CreatorConfig;
};

const achieved = (
  authorizations: CreatorAuthorizations | undefined,
  key: string
) => !!authorizations?.[key]?.achieved;

const buildUserProperties = (creatorConfig?: CreatorConfig) => {
  const authorizations = creatorConfig?.authorizations;
  const creatorOnboarded = creatorConfig?.csm_details?.creator_onboarded;

  return {
    is_boi_flipkart: creatorConfig?.is_boi_flipkart,
    account_type: creatorConfig?.account_type,
    creator_onboarded: creatorOnboarded,
    creator_username: creatorConfig?.authenticated_creator_username,
    sourcing_access: achieved(authorizations, "is_sourcing_enabled"),
    is_engage_active: achieved(authorizations, "is_engage_active"),
    firebase_id: creatorConfig?.firebase_id,
    ...(creatorOnboarded
      ? {
          csm_cohort: creatorConfig?.csm_details?.cohort,
          csm_sub_cohort: creatorConfig?.csm_details?.sub_cohort,
        }
      : {}),
    first_post: achieved(authorizations, "FIRST_POST"),
    social_login_complete: achieved(authorizations, "SOCIAL_LOGIN"),
    first_collection: achieved(authorizations, "FIRST_COLLECTION"),
    creator_profile: achieved(authorizations, "PROFILE_COMPLETED"),
    first_sale: achieved(authorizations, "FIRST_SALE"),
    earnings_cross_2k: achieved(authorizations, "EARNINGS_CROSS_2K"),
    engage_connected: achieved(authorizations, "ENGAGE_CONNECTED"),
    first_link_created: achieved(authorizations, "FIRST_LINK_CREATED"),
  };
};

/**
 * Identifies the creator to every analytics SDK and keeps their user
 * properties in sync. One component because all four vendors consume the
 * same derived property set.
 */
const CreatorAnalyticsIdentity = ({ creatorConfig }: Props) => {
  const { setUserIdInSingular } = useSingular();
  const { setUserIdClarity, setUserPropertiesClarity } = useClarity();
  const { initPlotline, setUserPropertiesPlotline } = usePlotline();
  const [triggerHandlePlotlineEventsData] =
    useHandlePlotlineEventsDataMutation();

  useEffect(() => {
    const creatorId = creatorConfig?.creator_id;
    if (!creatorId) return;

    const phoneNumber = creatorConfig?.phone_number;
    const userProperties = buildUserProperties(creatorConfig);

    triggerGAUserId(creatorId);
    setUserIdClarity(String(creatorId));
    if (phoneNumber) {
      setUserIdInSingular(
        String(phoneNumber),
        userProperties.creator_username || ""
      );
    }

    setUserPropertiesGa(userProperties);
    setUserPropertiesClarity(userProperties);

    if (phoneNumber && userProperties.social_login_complete) {
      initPlotline(
        String(phoneNumber),
        (eventName: string, properties: Record<string, unknown>) =>
          triggerHandlePlotlineEventsData({ ...properties, eventName }),
        { creatorId, creatorUsername: userProperties.creator_username }
      );
      setUserPropertiesPlotline({ ...userProperties, creator_id: creatorId });
    }
  }, [creatorConfig]);

  return null;
};

export default CreatorAnalyticsIdentity;
